/**
 * parseAdditionalProperty.ts
 *
 * Converte os campos productBenefits e technicalInformation
 * (HTML solto vindo do payload do produto) em um array de
 * additionalProperty (PropertyValue) para uso no JSON-LD.
 *
 * Não depende de bibliotecas de parse de DOM (cheerio/jsdom) —
 * usa regex simples, já que a estrutura do HTML de origem é
 * previsível e controlada internamente (sempre <h3>título</h3><p>descrição</p>).
 * Se preferir mais robustez, troque a extração por cheerio.
 */

/**
 * Extrai pares título/descrição de um bloco HTML no formato:
 * <h3 class="...title">Título</h3><p class="...description">Descrição</p>
 *
 * Funciona tanto para productBenefits (benefit-title/benefit-description)
 * quanto para technicalInformation (technical-title/technical-description),
 * pois o regex casa qualquer classe terminada em "-title" e "-description".
 *
 * @param {string} html - HTML bruto (productBenefits ou technicalInformation)
 * @returns {Array<{name: string, value: string}>}
 */
type ParsedPair = {
  name: string
  value: string
}

type AdditionalPropertyValue = {
  '@type': 'PropertyValue'
  name: string
  value: string
}

export function extractTitleDescriptionPairs(html: string): ParsedPair[] {
  if (!html || typeof html !== 'string') return []

  const pairs: ParsedPair[] = []

  // Casa qualquer <h3 class="*-title">...</h3> seguido, em algum ponto,
  // por <p class="*-description">...</p>, ignorando outras tags no meio (como <img>).
  const regex =
    /<h3[^>]*class="[^"]*-title"[^>]*>([\s\S]*?)<\/h3>[\s\S]*?<p[^>]*class="[^"]*-description"[^>]*>([\s\S]*?)<\/p>/g

  let match

  while ((match = regex.exec(html)) !== null) {
    const name = stripHtml(match[1]).trim()
    const value = stripHtml(match[2]).trim()

    if (name && value) {
      pairs.push({ name, value })
    }
  }

  return pairs
}

/**
 * Remove tags HTML residuais e normaliza entidades básicas.
 * @param {string} str
 * @returns {string}
 */
function stripHtml(str: string): string {
  return str
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Gera o array additionalProperty (PropertyValue) a partir de
 * productBenefits e technicalInformation, deduplicando por name
 * (mantendo a primeira ocorrência).
 *
 * @param {string} productBenefits - HTML de productBenefits
 * @param {string} technicalInformation - HTML de technicalInformation
 * @returns {Array<{"@type": "PropertyValue", name: string, value: string}>}
 */

export function buildAdditionalProperty(
  productBenefits: string,
  technicalInformation: string
): AdditionalPropertyValue[] {
  const allPairs = [
    ...extractTitleDescriptionPairs(productBenefits),
    ...extractTitleDescriptionPairs(technicalInformation),
  ]

  const seenNames = new Set<string>()
  const additionalProperty: AdditionalPropertyValue[] = []

  for (const { name, value } of allPairs) {
    const key = name.toLowerCase()

    if (seenNames.has(key)) continue // mantém a primeira ocorrência

    seenNames.add(key)

    additionalProperty.push({
      '@type': 'PropertyValue',
      name,
      value,
    })
  }

  return additionalProperty
}
